import { Center, Spinner, Text, VStack } from "@biblioteksentralen/react";
import { useRouter } from "next/navigation";
import { useEffect } from "react";
import { LoginForm } from "../../rediaPlatform/LoginForm";
import { useRediaPlatformContext } from "../../rediaPlatform/RediaPlatformProvider";
import { getPath } from "@libry-content/common";
import { useTranslation } from "../../utils/hooks/useTranslation";
import { DefaultContainer } from "../ContentContainer";
import { ResolvedDigitalLibraryService } from "@libry-content/common";
import { isAuthenticatedService } from "./utils";

type DigitalLibraryServiceProps = {
  digitalLibraryService: ResolvedDigitalLibraryService | undefined;
};

export const DigitalLibraryServiceLogin = ({ digitalLibraryService }: DigitalLibraryServiceProps) => {
  const { user, isSessionReady, rediaPlatformIsEnabled } = useRediaPlatformContext();
  const { t, ts } = useTranslation();
  const router = useRouter();

  const { authenticatedServiceUrl, authenticatedServiceLoginInfo } = isAuthenticatedService(digitalLibraryService)
    ? digitalLibraryService
    : { authenticatedServiceUrl: undefined, authenticatedServiceLoginInfo: undefined };

  const digitalLibraryServicePath = digitalLibraryService ? getPath(digitalLibraryService!) : undefined;

  useEffect(() => {
    if (typeof window === "undefined" || !digitalLibraryServicePath) return;

    if (!rediaPlatformIsEnabled || !authenticatedServiceUrl) {
      // Denne ruten skal egentlig ikke lenkes til for tjenester uten 'authenticatedServiceUrl'.
      // Hvis noen allikevel forviller seg inn hit, omdirigerer vi bare tilbake til tjenestesiden.
      router.replace(digitalLibraryServicePath);
      return;
    }

    if (user) {
      // Merk: Referer settes bare riktig hvis Referrer-Policy er satt som header. Dette styres i next.config.js
      window.location.replace(authenticatedServiceUrl);
      // Merk: Bruker ikke `router.replace()` fordi den (per oktober 2023) brekker tilbakenavigasjon ved å
      // legge til ruten i nettleserhistorikken (location.push) istedenfor å erstatte den (location.replace).
    }
  }, [router, user, isSessionReady, authenticatedServiceUrl, digitalLibraryServicePath, rediaPlatformIsEnabled]);

  if (!rediaPlatformIsEnabled || !authenticatedServiceUrl) return null;

  if (!user)
    return (
      <DefaultContainer marginY="2rem">
        <VStack alignItems="center" justifyContent="center" gap="2rem">
          {authenticatedServiceLoginInfo && <Text>{ts(authenticatedServiceLoginInfo)}</Text>}
          <LoginForm />
        </VStack>
      </DefaultContainer>
    );

  return (
    <Center marginTop="3rem">
      <VStack alignItems="center" justifyContent="center" padding="1rem 1.5rem 1rem">
        <Spinner />
        {authenticatedServiceUrl && (
          <a href={authenticatedServiceUrl} referrerPolicy="unsafe-url">
            {t("Sender deg videre til tjenesten")}
          </a>
        )}
      </VStack>
    </Center>
  );
};
